<script setup>
import ymGoal from "@/helpers/goal.js";
import cookie from "js-cookie";
import { ref, onMounted, onUnmounted, computed, getCurrentInstance, watchEffect, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import api from "@/api.js";
import { useI18n } from "vue-i18n";
import Loader from "@/components/Loader.vue";
import Lang from "@/components/Lang.vue";
import { captureMessage } from "@/helpers/sentry.js";
import { storeToRefs } from "pinia";
import { useBookingFlow } from "@/stores/bookingFlowStore";
import { useIframeEmbed } from "@/stores/iframeEmbedStore";
import { getSegmentId } from "@/helpers/segment/getSegmentId";
import quizList from "@/common/quiz";
import { parseCustomTags, setCustomTags } from "@/helpers/customTags";
import { useToast } from "vue-toastification";

const { locale } = useI18n({ useScope: "global" });
const router = useRouter();
const route = useRoute();

const isRtl = computed(() => locale.value === "ar");
const isQuizV2 = computed(() => {
  return String(route.name || "").includes("V2") || String(route.path || "").startsWith("/v2/quiz/");
});

const toast = useToast();
watch(isRtl, (rtl) => {
  toast.updateDefaults({ rtl });
});

// Booking flow store
const bookingFlow = useBookingFlow();
bookingFlow.init();

// iframeEmbed
const { ifHideHeader, ifIframe } = storeToRefs(useIframeEmbed());

const app = getCurrentInstance();
const luxonSettings = app.appContext.config.globalProperties.$luxonSettings;
const loader = ref(true);
const segment = ref({});
const successPage = ref(cookie.get("success_page"));

watchEffect(() => {
  successPage.value = cookie.get("success_page");
});


const changeLangFromSelect = (lang) => {
  loader.value = true;
  locale.value = lang;
  luxonSettings.defaultLocale = lang;
  setTimeout(() => {
    loader.value = false;
  }, 100);
};

const checkSpeed = () => {
  let timeStart, endTime, timeDuration;
  timeStart = new Date().getTime();
  api.speedTest().then(() => {
    endTime = new Date().getTime();
    timeDuration = (endTime - timeStart) / 1000;
    const loadedBits = 1048576 * 8;
    const bps = (loadedBits / timeDuration).toFixed(2);
    const speedInKbps = (bps / 1024).toFixed(2);
    const speedInMbps = (speedInKbps / 1024).toFixed(2);
    cookie.set("speed_internet", speedInMbps);
  });
};

checkSpeed();

const isDetailPage = computed(() => route.name === "Detail");

const isQuiz = computed(() => route.meta.quiz);
const isLanding = computed(() => route.meta.landing);

const scrollingGoals = {
  infoLesson: false,
  hero: false,
  projects: false,
  numbers: false,
  meet: false,
  reviews: false,
  signup: false,
  footer: false,
};
const scrolling = (e) => {
  const setGoal = (entries, id, key) => {
    if (entries[0].target.id === id && scrollingGoals[key] === false) {
      switch (id) {
        case "hero":
          ymGoal("viewed_page_freecodingsession");
          break;
        case "info-lesson":
          ymGoal("viewed_page_whatwedoinlesson");
          break;
        case "projects":
          ymGoal("viewed_page_wewillcreate");
          break;
        case "numbers":
          ymGoal("viewed_page_aboutusinnumber");
          break;
        case "meet":
          ymGoal("viewed_page_meetyourtutor");
          break;
        case "reviews":
          ymGoal("viewed_page_trustatfirstsight");
          break;
        case "signup":
          ymGoal("viewed_page_singupforafree");
          break;
        case "footer":
          ymGoal("viewed_footer");
          break;
      }
      scrollingGoals[key] = true;
    }
  };
  const callbackForObserver = function (entries) {
    if (entries[0].isIntersecting) {
      setGoal(entries, "hero", "hero");
      setGoal(entries, "info-lesson", "infoLesson");
      setGoal(entries, "projects", "projects");
      setGoal(entries, "numbers", "numbers");
      setGoal(entries, "meet", "meet");
      setGoal(entries, "reviews", "reviews");
      setGoal(entries, "signup", "signup");
      setGoal(entries, "footer", "footer");
    }
  };

  const observer = new IntersectionObserver(callbackForObserver, {
    threshold: 0.3,
  });

  const blockHero = document.querySelector("#hero");
  const blockInfoLesson = document.querySelector("#info-lesson");
  const blockProjects = document.querySelector("#projects");
  const blockNumbers = document.querySelector("#numbers");
  const blockMeet = document.querySelector("#meet");
  const blockReviews = document.querySelector("#reviews");
  const blockSignup = document.querySelector("#signup");
  const blockFooter = document.querySelector("#footer");

  if (blockHero) observer.observe(blockHero);
  if (blockInfoLesson) observer.observe(blockInfoLesson);
  if (blockProjects) observer.observe(blockProjects);
  if (blockNumbers) observer.observe(blockNumbers);
  if (blockMeet) observer.observe(blockMeet);
  if (blockReviews) observer.observe(blockReviews);
  if (blockSignup) observer.observe(blockSignup);
  if (blockFooter) observer.observe(blockFooter);
};

onMounted(async () => {
  successPage.value = cookie.get("success_page");

  // window.gtagNew("event", "conversion", { send_to: "AW-11031098938/jTYFCNGmoYYYELrshIwp" });
  window.addEventListener("scroll", scrolling);

  const segmentId = getSegmentId();
  if (!segmentId) {
    // Send Sentry
    const href = document.location.href;
    const clientCookies = document.cookie;
    const referrer = document.referrer;
    console.log("Cookie: ", JSON.stringify(clientCookies), "---");
    console.log("href: ", JSON.stringify(href), "---");
    console.log("referer: ", JSON.stringify(referrer), "---");
    captureMessage(`BackupForm. No segment error`);
    router.push("/backup-form");
    loader.value = false;
    return;
  }

  const segmentInfo = await api.getSegmentInfo();

  if (!segmentInfo) {
    cookie.set("autobookingLang", "en");
    // Send Sentry
    const href = document.location.href;
    const clientCookies = document.cookie;
    const referrer = document.referrer;
    console.log("Cookie: ", JSON.stringify(clientCookies), "---");
    console.log("href: ", JSON.stringify(href), "---");
    console.log("referer: ", JSON.stringify(referrer), "---");
    captureMessage(`BackupForm. Get segment error`);
    loader.value = false;
    router.push("/backup-form");
    return;
  }

  cookie.set("client_segment_timezone_name", segmentInfo ? segmentInfo.timezone_name : "");
  cookie.set("client_segment_lang", segmentInfo ? segmentInfo.default_lang : "en");
  cookie.set("autobookingLang", segment.value ? segmentInfo.default_lang : "en");
  if (route.params.id) {
    cookie.set("quiz_id", route.params.id);
  }
  segment.value = segmentInfo;
  if (cookie.get("autobookingLang")) {
    locale.value = cookie.get("autobookingLang");
  } else {
    cookie.set("autobookingLang", segment.value ? segmentInfo.default_lang : "en");
    locale.value = segmentInfo.default_lang;
  }
  if (isQuiz.value) locale.value = segmentInfo.default_lang;
  luxonSettings.defaultLocale = locale.value ? locale.value : "en";

  // Save segment's timezone_name
  cookie.set("segment_timezone_name", segmentInfo.timezone_name);

  loader.value = false;

  if (route.query.country) {
    cookie.set("countryCode", route.query.country);
  }

  if (route.query.landingID) {
    cookie.set("landing_id", route.query.landingID);
  } else {
    const quizLandingId = quizList[route.params.id]?.parameters?.landing_id;
    if (quizLandingId) cookie.set("landing_id", quizLandingId);
  }

  setCustomTags(
    parseCustomTags(route.query.custom_tags ?? quizList[route.params.id]?.parameters?.custom_tags)
  );

  if (route.query.landing_type) {
    cookie.set("landing_type", route.query.landing_type);
  }

  if (route.query.landing_type_referrer) {
    cookie.set("landing_type_referrer", route.query.landing_type_referrer);
  }

  if (route.query.distribution_tag_ids) {
    var decodedString = decodeURIComponent(route.query.distribution_tag_ids);

    if (decodedString !== null) {
      var numberArray = decodedString.split(",").map(Number);

      // Convert the numberArray to an array of integers
      var integerArray = numberArray.map(function (num) {
        const parsedNum = parseInt(num, 10);
        if (!isNaN(parsedNum)) {
          return parsedNum;
        } else {
          return 0;
        }
      });

      cookie.set("distribution_tag_ids", JSON.stringify(integerArray));
    }
  }

  if (route.query.success_page) {
    cookie.set("success_page", route.query.success_page);
  }

  const localeUser = cookie.get("autobookingLang")

  if (!route.query.success_page && !cookie.get("success_page") && (localeUser == 'ru')) {
    cookie.set("success_page", "ru_thankyou_page_tg");
  }
});

onUnmounted(() => {
  window.removeEventListener("scroll", scrolling);
});

</script>

<template>
  <div
    v-if="isQuiz || isLanding"
    :class="{ 'content-quiz': isQuiz, 'content-quiz-v2': isQuiz && isQuizV2 }"
    :dir="isRtl ? 'rtl' : 'ltr'">
    <loader v-if="isLanding && loader" size="100"></loader>
    <router-view />
  </div>
  <template v-else>
    <div
      ref="autobooking-form"
      class="content"
      :class="{ iframe: ifIframe }"
      :dir="isRtl ? 'rtl' : 'ltr'">
      <header class="header" v-if="!ifHideHeader">
        <img class="logo" src="/logo.svg" alt="logo" />
        <lang v-if="!loader && !route.meta.success" :default-lang-segment="segment ? segment.default_lang : 'en'"
          @change="changeLangFromSelect" />
      </header>
      <loader v-if="loader" size="80" />
      <div v-else>
        <router-view />
      </div>
    </div>
    <img v-if="!ifHideHeader" class="bg-line" src="/bg-line.png" />
  </template>
</template>

<style lang="scss">
@import "./assets/styles/fonts.css";
@import "./assets/styles/global.scss";

.content.iframe {
  height: fit-content !important;
  padding: 20px !important;
}
.content.iframe .title.usersmc-content__title {
  margin-top: 0 !important;
}

.logo {
  width: 200px;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
</style>
